import { useEffect, useState } from 'react';
import { LockIcon, RepeatClockIcon, UnlockIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useDoorData } from './context/DoorContext';
import configData from '../clientNustatymai';

const SERVER_IP = configData.SERVER_IP;

function DoorItemIp() {
  const {
    openDoor,
    openLongDoor,
    closeDoor,
    loading,
    getDoorStatusBadge,
    systemDoors,
    axiosJWT,
    data,
  } = useDoorData();

  const [ipByDoor, setIpByDoor] = useState({});
  const [doorStatusByIp, setDoorStatusByIp] = useState({});

  // 1) DoorID -> IP map
 useEffect(() => {
  if (!systemDoors || systemDoors.length === 0) return;

  const map = {}; // JS, be tipų

  for (const door of systemDoors) {
    const raw = door?.DoorSerial;

    const serial = raw == null ? '' : String(raw).trim();

    const isEmpty =
      serial === '' ||
      serial.toLowerCase() === 'null';

    map[door.DoorID] = isEmpty ? SERVER_IP : serial;
  }

  setIpByDoor(map);
}, [systemDoors]);


  // 2) helperis statusams iš IP
  const reloadStatusesFromIps = async () => {
    if (!ipByDoor || Object.keys(ipByDoor).length === 0) return;

    const statusMap = {};
    const uniqueIps = [...new Set(Object.values(ipByDoor))];

    for (const ip of uniqueIps) {
      if (ip === SERVER_IP) continue; // savo serverio neimam – jo statusas yra "data"

      try {
        const res = await axiosJWT.get(`http://${ip}:3000/doors_status`);
        console.log(`✅ Status fetched from IP ${ip}:`, res.data);
        const list = res.data || [];

        const doorsForIp = Object.entries(ipByDoor)
          .filter(([_, doorIp]) => doorIp === ip)
          .map(([doorID]) => Number(doorID))
          .sort((a, b) => a - b);

        doorsForIp.forEach((globalDoorId, idx) => {
          const remoteDoor = list[idx];
          statusMap[globalDoorId] = remoteDoor?.doorStatus || 'UNKNOWN';
        });
      } catch (err) {
        console.error(`❌ Status fetch fail IP ${ip}`, err);
        for (const [doorID, doorIp] of Object.entries(ipByDoor)) {
          if (doorIp === ip) {
            statusMap[Number(doorID)] = 'OFFLINE';
          }
        }
      }
    }

    setDoorStatusByIp(statusMap);
  };

  // 3) foninis tikrinimas kas 3s
  useEffect(() => {
    if (!ipByDoor || Object.keys(ipByDoor).length === 0) return;

    reloadStatusesFromIps(); // pirmą kartą iškart

    const intervalId = setInterval(() => {
      reloadStatusesFromIps();
    }, 3000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipByDoor]);

  // fallback statusai iš pagrindinio serverio
  const statusByDoorId = {};
  for (const d of data || []) {
    statusByDoorId[d.doorID] = d.doorStatus;
  }

  // unikalios durys
  const uniqueDoors = [];
  const seen = new Set();
  for (const door of systemDoors || []) {
    if (!seen.has(door.DoorID)) {
      seen.add(door.DoorID);
      uniqueDoors.push(door);
    }
  }

  // 👇 RŪŠIAVIMAS: pirma isMainDoor = 1, po to kitos
  uniqueDoors.sort((a, b) => {
    const aMain = a.isMainDoor === 1 || a.IsMainDoor === 1;
    const bMain = b.isMainDoor === 1 || b.IsMainDoor === 1;

    if (aMain && !bMain) return -1;
    if (!aMain && bMain) return 1;

    // jei abu vienodi pagal isMainDoor – pagal DoorID
    return a.DoorID - b.DoorID;
  });

  const isLoading = loading || !uniqueDoors || uniqueDoors.length === 0;

  return (
    <Grid
      mx="5vw"
      gridRow="3 / 4"
      gap={5}
      templateColumns={{
        base: '1fr',
        lg: 'repeat(2, minmax(420px, 1fr))',
      }}
      justifyItems="stretch"
    >
      {!isLoading ? (
        uniqueDoors.map((door) => {
          const doorID = door.DoorID;
          const ip = ipByDoor[doorID] || SERVER_IP;

          let statusKey;
          if (doorStatusByIp[doorID] === 'OFFLINE') {
            statusKey = 'OFFLINE';
          } else if (doorStatusByIp[doorID]) {
            statusKey = doorStatusByIp[doorID];
          } else if (statusByDoorId[doorID]) {
            statusKey = statusByDoorId[doorID];
          } else {
            statusKey = 'NOT_FOUND';
          }

          const doorStatus = getDoorStatusBadge(statusKey);

          return (
            <GridItem key={doorID} w="100%">
              <HStack
                w="100%"
                position="relative"
                overflow="hidden"
                bg="#f5f5f5"
                borderRadius="1rem"
                justifyContent="space-between"
                px={4}
                py={3}
              >
                {/* KAIRĖ – ID + pavadinimas + IP */}
                <HStack spacing={4}>
                  <Tooltip hasArrow label={doorStatus.text} aria-label="Durų būsena">
                    <Box
                      w="52px"
                      h="52px"
                      borderRadius="999px"
                      bg={doorStatus.bgColor}
                      display="flex"
                      flexDir="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text color="#fff" fontWeight="700" fontSize="md">
                        {doorID}
                      </Text>
                      <Text as={doorStatus.icon} color="#fff" fontSize="xs" />
                    </Box>
                  </Tooltip>

                  <VStack align="flex-start" spacing={0}>
                    <Text
                      color="#1A202C"
                      fontWeight="600"
                      textTransform="uppercase"
                      fontSize="1rem"
                      noOfLines={1}
                      maxW="220px"
                    >
                      {door.DoorName}
                    </Text>
                    <Text color="#4A5568" fontSize="0.8rem">
                      IP: {ip}
                    </Text>
                  </VStack>
                </HStack>

                {/* DEŠINĖ – mygtukai */}
                <HStack spacing={1}>
                  <Tooltip label="Atrakinti keliom sekundėm">
                    <IconButton
                      size="md"
                      borderRadius="0.75rem 0 0 0.75rem"
                      bg="green.500"
                      color="#fff"
                      _hover={{ bg: 'green.600' }}
                      aria-label="Atrakinti"
                      icon={<UnlockIcon />}
                      onClick={async () => {
                        await openDoor(doorID);
                        await reloadStatusesFromIps();
                      }}
                    />
                  </Tooltip>

                  <Tooltip label="Užrakinti duris">
                    <IconButton
                      size="md"
                      bg="red.500"
                      color="#fff"
                      _hover={{ bg: 'red.600' }}
                      aria-label="Užrakinti"
                      icon={<LockIcon />}
                      onClick={async () => {
                        await closeDoor(doorID);
                        await reloadStatusesFromIps();
                      }}
                    />
                  </Tooltip>

                  <Tooltip label="Palikti atrakintą visam laikui">
                    <IconButton
                      size="md"
                      borderRadius="0 0.75rem 0.75rem 0"
                      bg="orange.500"
                      color="#fff"
                      _hover={{ bg: 'orange.600' }}
                      aria-label="Atrakinti ilgam"
                      icon={<RepeatClockIcon />}
                      onClick={async () => {
                        await openLongDoor(doorID);
                        await reloadStatusesFromIps();
                      }}
                    />
                  </Tooltip>
                </HStack>
              </HStack>
            </GridItem>
          );
        })
      ) : (
        <Flex w="90vw" alignItems="center" justifyContent="center">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="green.500"
            size="xl"
          />
        </Flex>
      )}
    </Grid>
  );
}

export default DoorItemIp;
